import { useCallback, ReactNode, useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useUsersQuery } from "$hooks/useUsersQuery";
import { Warehouse, CompanyType } from "$types";
import { EntrepotService, CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

export const usePatchWarehouse = ({
  companyId,
  redirect,
  selectedTab = [],
  contributors = [],
  clients = [],
}) => {
  const { data: usersData } = useUsersQuery();

  const { data: warehouses, isLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_ENTREPOTS, { company: companyId }],
    queryFn: () => EntrepotService.getEntrepots({ company: companyId }),
    enabled: !!companyId,
    staleTime: 1000 * 60,
  }) as any;

  const ownWarehouses = useMemo(
    () => (warehouses?.data || []).filter((e) => e.parentCompany === companyId),
    [companyId, warehouses]
  );
  const [companies, setCompanies] = useState([]);

  const fecthCompanies = useCallback(async () => {
    const filters = {
      page: 1,
      rowsPerPage: 50000,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      companies = companies.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  const renderCell = useCallback(
    (value: ReactNode, entrepot: Warehouse) => ({
      value,
      onClickAction: () =>
        redirect(`/entreprise/${companyId}/${entrepot?._id}`),
    }),
    [companyId, redirect]
  );

  const renderWarehouseBaseData = useCallback(
    (e: Warehouse) => {
      const users = usersData || [];
      return [
        renderCell(e.name, e),
        renderCell(e?.location?.adress || "", e),
        renderCell(e?.location?.cp || "", e),
        renderCell(e?.location?.city || "", e),
        renderCell(
          users
            .filter((user) => user.warehouse === e._id && !user.isDisable)
            .map((u) => `${u.firstname} ${u.lastname}`)
            .join(" | "),
          e
        ),
      ];
    },
    [renderCell, usersData]
  );

  const renderOtherWarehouses = useCallback(
    (entrepots: Warehouse[]) => {
      if (!entrepots) {
        return [];
      }
      return entrepots.map((e) => [
        ...renderWarehouseBaseData(e),
        renderCell(companies.find((c) => c._id === e.parentCompany)?.name, e),
      ]);
    },
    [companies, renderCell, renderWarehouseBaseData]
  );

  let missing = "";
  let renderWareHouse;

  const renderClientWarehouse = renderOtherWarehouses(
    (warehouses?.data || []).filter((e) =>
      clients.map((client) => client._id).includes(e.parentCompany)
    )
  );

  const renderContributorWareHouse = renderOtherWarehouses(
    // first filter
    (warehouses?.data || [])
      .filter(
        (e) =>
          contributors
            .map((contributor) => contributor._id)
            .includes(e.parentCompany)
        // entrepots must have current company in their companies
      )
      .filter((e) => e.companies?.find((c) => c._id === companyId))
  );

  const renderLogisticianWareHouse = renderOtherWarehouses(
    (warehouses?.data || []).filter(
      (e) =>
        (e.companies?.some((company) => companyId === company._id) ?? false) &&
        companies
          ?.find((c) => c._id === e.parentCompany)
          ?.companyTypes?.includes(CompanyType.LOGISTICIAN)
    )
  );

  switch (true) {
    case selectedTab.includes(CompanyType.CLIENT):
      missing = "au client";
      renderWareHouse = ownWarehouses?.map((e) => renderWarehouseBaseData(e));
      break;
    case selectedTab.includes(CompanyType.CONTRIBUTOR):
      missing = "à l'intervenant";
      renderWareHouse = ownWarehouses?.map((e) => [
        ...renderWarehouseBaseData(e),
        renderCell(
          companies
            .filter((c) =>
              e.companies.some(
                (entrepotCompany) => entrepotCompany._id === c._id
              )
            )
            .map((company) => company.name)
            .join(" | "),
          e
        ),
      ]);
      break;
    case selectedTab.includes(CompanyType.LOGISTICIAN):
      missing = "au logisticien";
      renderWareHouse = ownWarehouses?.map((e) => renderWarehouseBaseData(e));
      break;
    default:
      missing = "à l'entreprise";
      break;
  }

  return {
    isLoading,
    missing,
    renderWareHouse,
    renderContributorWareHouse,
    renderLogisticianWareHouse,
    renderClientWarehouse,
  };
};
